import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  FolderOpen, CheckCircle, Clock, Warning, ArrowRight, CaretLeft,
  FileText, User, CalendarBlank, CheckSquare, Square, Eye, DownloadSimple,
  ListChecks, CurrencyDollar, ClockCounterClockwise
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUT_CONFIG = {
  en_attente: { label: 'En attente', color: '#FF8E00', bg: 'bg-amber-100 text-amber-700' },
  en_cours: { label: 'En cours', color: '#00AEEF', bg: 'bg-blue-100 text-blue-700' },
  valide: { label: 'Valide', color: '#2D5A4C', bg: 'bg-emerald-100 text-emerald-700' },
  refuse: { label: 'Refuse', color: '#EA3C3A', bg: 'bg-red-100 text-red-700' },
  cloture: { label: 'Cloture', color: '#5E6D66', bg: 'bg-gray-100 text-gray-600' },
};

const TYPE_LABELS = {
  accompagnement: 'Accompagnement',
  creation: 'Creation',
  transformation: 'Transformation',
  adhesion: 'Adhesion',
  formation: 'Formation',
};

export default function ClientDossiersPage() {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchDossiers();
  }, [filter]);

  const fetchDossiers = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?statut=${filter}` : '';
      const { data } = await axios.get(`${API}/api/client/dossiers${params}`, { withCredentials: true });
      setDossiers(data.items || []);
    } catch {
      // Silently fail - likely unauthorized redirect handled by ClientLayout
    }
    setLoading(false);
  };

  const openDetail = async (dossierId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const { data } = await axios.get(`${API}/api/client/dossiers/${dossierId}`, { withCredentials: true });
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement dossier');
    }
    setDetailLoading(false);
  };

  const handleDocDownload = async (doc) => {
    try {
      const { data } = await axios.get(`${API}/api/client/documents/${doc.id}/download`, { withCredentials: true });
      if (data.download_url) {
        window.open(data.download_url, '_blank');
      }
    } catch {
      toast.error('Erreur telechargement');
    }
  };

  const filters = [
    { key: 'all', label: 'Tous' },
    { key: 'en_cours', label: 'En cours' },
    { key: 'en_attente', label: 'En attente' },
    { key: 'valide', label: 'Valides' },
  ];

  const getChecklist = (d) => {
    const items = d.checklist || [];
    const done = items.filter(c => c.checked).length;
    return { items, done, total: items.length, pct: items.length > 0 ? Math.round((done / items.length) * 100) : 0 };
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#00AEEF]" /></div>;
  }

  // Detail view
  if (detail) {
    const s = STATUT_CONFIG[detail.statut] || STATUT_CONFIG.en_attente;
    const cl = getChecklist(detail);
    return (
      <div className="space-y-5 animate-fade-in" data-testid="client-dossier-detail">
        <button onClick={() => setDetail(null)} className="flex items-center gap-1.5 text-xs text-[#5E6D66] hover:text-[#1A2421] transition-colors" data-testid="back-to-dossiers">
          <CaretLeft size={14} /> Retour aux dossiers
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs text-[#5E6D66]">{detail.reference}</span>
              <Badge className={`text-[10px] ${s.bg}`}>{s.label}</Badge>
            </div>
            <h1 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }} data-testid="dossier-detail-title">{detail.titre || detail.reference}</h1>
            <p className="text-sm text-[#5E6D66] mt-1">{detail.description || ''}</p>
          </div>
          <div className="flex flex-col items-end gap-1 text-xs text-[#5E6D66]">
            {detail.assigned_to_name && (
              <span className="flex items-center gap-1"><User size={12} /> {detail.assigned_to_name}</span>
            )}
            {detail.type_dossier && (
              <Badge variant="outline" className="text-[10px]">{TYPE_LABELS[detail.type_dossier] || detail.type_dossier}</Badge>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Checklist */}
          <Card className="border-[#E8E6E1] lg:col-span-2" data-testid="dossier-checklist">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
                <ListChecks size={16} className="text-[#2D5A4C]" />
                Checklist ({cl.done}/{cl.total})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cl.total === 0 ? (
                <p className="text-sm text-[#5E6D66] py-4 text-center">Aucun element dans la checklist</p>
              ) : (
                <>
                  <div className="h-2 bg-[#E8E6E1] rounded-full mb-4 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${cl.pct >= 100 ? 'bg-emerald-500' : cl.pct >= 50 ? 'bg-[#2D5A4C]' : 'bg-[#FF8E00]'}`}
                      style={{ width: `${cl.pct}%` }}
                    />
                  </div>
                  <div className="space-y-2">
                    {cl.items.map((item) => (
                      <div key={item.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg ${item.checked ? 'bg-emerald-50/50' : 'bg-[#F9F8F5]'}`}>
                        {item.checked
                          ? <CheckSquare size={18} weight="fill" className="text-emerald-600 flex-shrink-0" />
                          : <Square size={18} className="text-[#E8E6E1] flex-shrink-0" />
                        }
                        <span className={`text-sm ${item.checked ? 'text-[#5E6D66] line-through' : 'text-[#1A2421]'}`}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Info sidebar */}
          <div className="space-y-4">
            <Card className="border-[#E8E6E1]" data-testid="dossier-info">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#5E6D66] uppercase font-bold">Progression</span>
                  <span className="text-lg font-bold" style={{ color: s.color, fontFamily: 'Manrope' }}>{cl.pct}%</span>
                </div>
                <div className="space-y-2 text-xs">
                  {detail.created_at && (
                    <div className="flex items-center gap-2 text-[#5E6D66]">
                      <CalendarBlank size={12} />
                      <span>Cree le {new Date(detail.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[#5E6D66]">
                    <FileText size={12} />
                    <span>{(detail.documents || []).length} document(s)</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#5E6D66]">
                    <CurrencyDollar size={12} />
                    <span>{(detail.payments || []).length} paiement(s)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Documents */}
        {(detail.documents || []).length > 0 && (
          <Card className="border-[#E8E6E1]" data-testid="dossier-documents">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
                <FileText size={16} className="text-[#00AEEF]" />
                Documents ({(detail.documents || []).length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E8E6E1]">
                    <TableHead className="text-[10px] uppercase text-[#5E6D66]">Document</TableHead>
                    <TableHead className="text-[10px] uppercase text-[#5E6D66]">Date</TableHead>
                    <TableHead className="text-[10px] uppercase text-[#5E6D66] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(detail.documents || []).map((doc) => (
                    <TableRow key={doc.id} className="border-[#E8E6E1]">
                      <TableCell className="text-xs text-[#1A2421]">{doc.titre || doc.original_filename}</TableCell>
                      <TableCell className="text-xs text-[#5E6D66]">{doc.created_at ? new Date(doc.created_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="h-7 text-[10px] text-[#00AEEF]" onClick={() => handleDocDownload(doc)}>
                          <DownloadSimple size={12} className="mr-1" /> Telecharger
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Activity log */}
        {(detail.activities || []).length > 0 && (
          <Card className="border-[#E8E6E1]" data-testid="dossier-activity">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
                <ClockCounterClockwise size={16} className="text-[#5E6D66]" />
                Historique
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(detail.activities || []).map((a, i) => (
                  <div key={a.id || i} className="flex items-start gap-3 py-2 border-b border-[#F0EFEB] last:border-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2D5A4C] mt-1.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#1A2421]">{a.description || a.action}</p>
                      <p className="text-[10px] text-[#5E6D66] mt-0.5">
                        {a.created_at ? new Date(a.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        {a.user_email && ` — ${a.user_email}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-dossiers-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Mes dossiers</h1>
        <p className="text-sm text-[#5E6D66] mt-1">Suivez l'avancement de vos dossiers et checklists</p>
      </div>

      {/* Filter buttons */}
      <div className="flex gap-2" data-testid="client-dossier-filters">
        {filters.map((f) => (
          <Button
            key={f.key}
            variant={filter === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setFilter(f.key); }}
            className={filter === f.key ? 'bg-[#2D5A4C] hover:bg-[#1E4136]' : 'border-[#E8E6E1]'}
            data-testid={`client-dossier-filter-${f.key}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {dossiers.length === 0 ? (
        <Card className="border-[#E8E6E1]">
          <CardContent className="py-16 text-center">
            <FolderOpen size={36} className="mx-auto text-[#E8E6E1] mb-3" />
            <p className="text-[#5E6D66]">Aucun dossier pour le moment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="client-dossiers-grid">
          {dossiers.map((d) => {
            const s = STATUT_CONFIG[d.statut] || STATUT_CONFIG.en_attente;
            const cl = getChecklist(d);
            return (
              <Card
                key={d.id}
                className="border-[#E8E6E1] cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
                onClick={() => openDetail(d.id)}
                data-testid={`client-dossier-card-${d.id}`}
              >
                <div className="h-1" style={{ background: s.color }} />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <span className="font-mono text-[10px] text-[#5E6D66]">{d.reference}</span>
                    <Badge className={`text-[9px] ${s.bg}`}>{s.label}</Badge>
                  </div>
                  <h3 className="text-sm font-semibold text-[#1A2421] mb-1 line-clamp-2">{d.titre || d.reference}</h3>
                  {d.type_dossier && (
                    <Badge variant="outline" className="text-[9px] mb-3">{TYPE_LABELS[d.type_dossier] || d.type_dossier}</Badge>
                  )}

                  {/* Checklist progress */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-[#5E6D66]">{cl.done}/{cl.total} etapes</span>
                      <span className="text-[10px] font-bold" style={{ color: s.color }}>{cl.pct}%</span>
                    </div>
                    <div className="h-2 bg-[#E8E6E1] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${cl.pct >= 100 ? 'bg-emerald-500' : cl.pct >= 50 ? 'bg-[#2D5A4C]' : 'bg-[#FF8E00]'}`}
                        style={{ width: `${cl.pct}%` }}
                      />
                    </div>
                  </div>

                  {/* Footer info */}
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#F0EFEB]">
                    <div className="flex items-center gap-3 text-[10px] text-[#5E6D66]">
                      <span className="flex items-center gap-0.5"><FileText size={10} /> {d.document_count || 0}</span>
                      {d.assigned_to_name && <span className="flex items-center gap-0.5"><User size={10} /> {d.assigned_to_name}</span>}
                    </div>
                    <ArrowRight size={12} className="text-[#5E6D66]" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {detailLoading && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 shadow-xl">
            <Loader2 className="h-6 w-6 animate-spin text-[#00AEEF] mx-auto" />
            <p className="text-xs text-[#5E6D66] mt-2">Chargement...</p>
          </div>
        </div>
      )}
    </div>
  );
}
